import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css'; // 代码高亮样式
import '../styles/ChatBot.css';
import FingerprintJS from '@fingerprintjs/fingerprintjs';

function ChatBot({ onClose, onQuotaChange }) {
  const [messages, setMessages] = useState(() => {
    // 从sessionStorage恢复聊天记录（仅在当前会话期间保存）
    const savedMessages = sessionStorage.getItem('matai_chat_messages');
    if (savedMessages) {
      try {
        return JSON.parse(savedMessages);
      } catch (error) {
        console.error('Error parsing saved messages:', error);
      }
    }
    // 默认空数组，欢迎消息将通过单独的状态显示
    return [];
  });
  const [showWelcome, setShowWelcome] = useState(() => {
    // 如果有保存的消息，不显示欢迎界面
    const savedMessages = sessionStorage.getItem('matai_chat_messages');
    return !savedMessages || savedMessages === '[]';
  });
  const [input, setInput] = useState('');
  const [quota, setQuota] = useState(() => {
    // 配额仍使用localStorage以便跨会话保存
    const saved = localStorage.getItem('chat_quota');
    return saved ? parseInt(saved, 10) : 50; // 默认50次
  });
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [copyStatus, setCopyStatus] = useState({ show: false, text: '' });
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  }, [messages]);

  // 保存聊天记录到sessionStorage
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('matai_chat_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // 粘贴事件处理
  useEffect(() => {
    const handlePaste = (e) => {
      // 检查是否有图片数据
      const items = e.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            e.preventDefault();
            const file = item.getAsFile();
            if (file) {
              handleImageFile(file);
            }
            break;
          }
        }
      }
    };

    // 添加粘贴事件监听
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, []);

  // 初始化 FingerprintJS，获取浏览器指纹作为用户 ID
  useEffect(() => {
    const initFingerprint = async () => {
      try {
        // 若 localStorage 已缓存指纹则直接使用（提高速度）
        const cachedId = localStorage.getItem('matai_user_id');
        if (cachedId) {
          setUserId(cachedId);
          return;
        }

        const fp = await FingerprintJS.load();
        const result = await fp.get();
        const id = result.visitorId; // 唯一浏览器指纹 ID
        setUserId(id);
        localStorage.setItem('matai_user_id', id);
      } catch (err) {
        console.error('FingerprintJS 初始化失败，回退到随机 ID:', err);
        // 回退方案：生成随机 ID 避免阻塞
        const fallbackId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        setUserId(fallbackId);
        localStorage.setItem('matai_user_id', fallbackId);
      }
    };

    initFingerprint();
  }, []);

  // 获取用户配额
  useEffect(() => {
    if (!userId) return; // 指纹尚未获取

    const fetchQuota = async () => {
      try {
        const response = await fetch(`/api/chat/quota/${userId}`);
        const data = await response.json();
        setQuota(data.remaining_quota);
        localStorage.setItem('chat_quota', data.remaining_quota.toString());
      } catch (error) {
        console.error('Error fetching quota:', error);
      }
    };
    fetchQuota();
  }, [userId]);

  // 当 quota 更新时通知父组件
  useEffect(() => {
    if (typeof onQuotaChange === 'function') {
      onQuotaChange(quota);
    }
  }, [quota, onQuotaChange]);

  const handleImageFile = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('Image size should be less than 10MB.');
      return;
    }

    setIsUploadingImage(true);
    setSelectedImage(file);
    
    // 创建预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setIsUploadingImage(false);
    };
    reader.onerror = () => {
      setIsUploadingImage(false);
      alert('Error loading image. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageFile(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setIsUploadingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 显示复制状态提示
  const showCopyStatus = (text) => {
    setCopyStatus({ show: true, text });
    setTimeout(() => {
      setCopyStatus({ show: false, text: '' });
    }, 2000);
  };

  // 复制代码块内容
  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      showCopyStatus('code has been copied');
    } catch (err) {
      console.error('Failed to copy code: ', err);
      showCopyStatus('Failed to copy code');
    }
  };

  // 复制整个消息的markdown内容
  const handleCopyMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopyStatus('Content has been copied');
    } catch (err) {
      console.error('Failed to copy message: ', err);
      showCopyStatus('Failed to copy message');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (input.trim() === '' && !selectedImage) return;
    if (!userId) return; // 指纹未就绪
    if (isUploadingImage) return; // 图片上传中时不能发送
    
    // 隐藏欢迎界面
    setShowWelcome(false);
    
    if (quota <= 0) {
      setMessages(prev => [...prev, { 
        id: prev.length + 1, 
        text: 'You have used up your quota of 50 conversations. Thank you for using MATAI Chat!', 
        isBot: true 
      }]);
      return;
    }

    const messageText = input.trim() || (selectedImage ? 'Uploaded an image for analysis' : '');
    const userMessage = { 
      id: messages.length + 1, 
      text: messageText, 
      isBot: false,
      hasImage: !!selectedImage,
      imagePreview: imagePreview
    };
    
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    
    const currentInput = input;
    const currentImage = selectedImage;
    setInput('');
    removeImage();

    try {
      let response;
      
      if (currentImage) {
        // 有图片时使用上传API
        const formData = new FormData();
        formData.append('file', currentImage);
        formData.append('message', currentInput);
        formData.append('user_id', userId);
        
        response = await fetch('/api/chat/upload', {
          method: 'POST',
          body: formData,
        });
      } else {
        // 纯文本对话
        response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: currentInput,
            user_id: userId
          }),
        });
      }

      const data = await response.json();
      
      if (data.success) {
        const botMessage = { 
          id: messages.length + 2, 
          text: data.response, 
          isBot: true 
        };
        setMessages(prev => [...prev, botMessage]);
        setQuota(data.remaining_quota);
        if (typeof onQuotaChange === 'function') {
          onQuotaChange(data.remaining_quota);
        }
        localStorage.setItem('chat_quota', data.remaining_quota.toString());
      } else {
        const errorMessage = { 
          id: messages.length + 2, 
          text: data.response || 'Sorry, I encountered an error. Please try again.', 
          isBot: true 
        };
        setMessages(prev => [...prev, errorMessage]);
      }
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        id: messages.length + 2, 
        text: 'Sorry, I encountered a connection error. Please check your network and try again.', 
        isBot: true 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="chatbot-container">
      <div className="messages-container">
        {showWelcome && (
          <div className="welcome-message">
            <h2>Hello! I'm MATAI Chat</h2>
            <p>Your materials science expert assistant. I can help you with questions about alloys, materials properties, microstructures, and more. You can also upload images for analysis.</p>
            
            <div className="welcome-commands">
              <p className="welcome-commands-hint">
                💡 <strong>Tip:</strong> I support special commands for direct property prediction and alloy design! 
                Click the "🚀 Commands" button below to see available formats.
              </p>
            </div>
            
            <p className="welcome-question">How can I assist you today?</p>
          </div>
        )}
        
        {messages.map(message => (
          <div 
            key={message.id} 
            className={`message ${message.isBot ? 'bot' : 'user'}`}
          >
            <div className="message-bubble">
              {message.hasImage && message.imagePreview && (
                <div className="message-image">
                  <img src={message.imagePreview} alt="Uploaded content" />
                </div>
              )}
              <div className="message-text">
                {message.isBot ? (
                  <div className="markdown-content">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeHighlight]}
                      components={{
                        // 自定义代码块组件
                        code({node, inline, className, children, ...props}) {
                          const match = /language-(\w+)/.exec(className || '');
                          return !inline && match ? (
                            <div className="code-block-wrapper">
                              <div className="code-block-header">
                                <span className="code-language">{match[1]}</span>
                                                                 <button 
                                   className="copy-code-btn"
                                   onClick={() => handleCopyCode(String(children).replace(/\n$/, ''))}
                                   title="Copy code"
                                 >
                                   <span className="copy-icon">📋</span>
                                 </button>
                              </div>
                              <code className={className} {...props}>
                                {children}
                              </code>
                            </div>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        },
                        // 自定义表格组件
                        table: ({children}) => (
                          <div className="markdown-table-wrapper">
                            <table className="markdown-table">{children}</table>
                          </div>
                        )
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                                         <button 
                       className="copy-message-btn"
                       onClick={() => handleCopyMessage(message.text)}
                       title="Copy message as markdown"
                     >
                     </button>
                  </div>
                ) : (
                  message.text
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message bot">
            <div className="message-bubble">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* 图片预览区域 */}
      {(imagePreview || isUploadingImage) && (
        <div className="image-preview-container">
          {isUploadingImage ? (
            <div className="image-upload-progress">
              <div className="upload-spinner"></div>
              <span>Uploading image...</span>
            </div>
          ) : (
            <div className="image-preview">
              <img src={imagePreview} alt="Preview" />
              <button className="remove-image-btn" onClick={removeImage}>×</button>
            </div>
          )}
        </div>
      )}

      <form className="input-container" onSubmit={handleSendMessage}>
        <div className="input-row">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={selectedImage ? "Add a message about your image..." : "Ask me about materials science..."}
            disabled={quota <= 0 || isLoading}
          />
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          
          <button 
            type="button" 
            className="image-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={quota <= 0 || isLoading || isUploadingImage}
            title="Upload image"
          >
            📷
          </button>
          
          <button 
            type="submit" 
            disabled={quota <= 0 || isLoading || isUploadingImage || (input.trim() === '' && !selectedImage)}
          >
            {isLoading ? '...' : 'Send'}
          </button>
        </div>
      </form>
      
      {/* 复制状态提示 */}
      {copyStatus.show && (
        <div className="copy-status-toast">
          <span>{copyStatus.text}</span>
        </div>
      )}

    </div>
  );
}

export default ChatBot;
